import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { FileText, ExternalLink, BookOpen, ShieldAlert } from "lucide-react";

export default function Forms() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/gov-forms").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="min-h-screen bg-slate-50"><Nav /></div>;

  const Card = ({ item, primary }) => (
    <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`gov-form-${item.ref}`}
      className={`group flex items-start gap-4 rounded-xl border bg-white p-5 transition-colors hover:shadow-sm ${primary ? "border-green-700 ring-1 ring-green-700/20" : "border-slate-200 hover:border-slate-400"}`}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${primary ? "bg-green-800 text-white" : "bg-slate-100 text-slate-600"}`}>
        <FileText className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-medium text-slate-600">{item.ref}</span>
          {primary && <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">MVP focus</span>}
        </div>
        <h3 className="mt-1.5 font-medium text-slate-900">{item.title}</h3>
        <p className="mt-1 text-sm text-slate-500">{item.description}</p>
        <p className="mt-2 flex items-center gap-1 text-xs text-green-800">Open official guidance <ExternalLink className="h-3 w-3" /></p>
      </div>
    </a>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Nav />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">Official sources</div>
        <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-900">Consumer rights & dispute guidance</h1>
        <p className="mt-3 max-w-2xl text-slate-600">Official UK sources covering faulty goods, poor services, refunds, cancellations, unfair terms and escalation routes.</p>
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <ShieldAlert className="h-4 w-4" /> Rules and deadlines can change. Check the linked official source before acting. Last checked: {data.last_checked}.
        </div>

        <h2 className="mt-10 font-serif text-2xl font-semibold text-slate-900">Core guidance</h2>
        <div className="mt-4 grid gap-3">{data.forms.map((f) => <Card key={f.ref} item={f} primary={f.primary} />)}</div>

        <h2 className="mt-10 flex items-center gap-2 font-serif text-2xl font-semibold text-slate-900"><BookOpen className="h-5 w-5" /> Guidance</h2>
        <div className="mt-4 grid gap-3">{data.guidance.map((g) => <Card key={g.ref} item={g} />)}</div>

        <a href={data.acas.url} target="_blank" rel="noopener noreferrer" data-testid="acas-link"
          className="mt-10 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 p-5 transition-colors hover:bg-blue-100">
          <div>
            <h3 className="font-medium text-blue-900">{data.acas.name}</h3>
            <p className="mt-1 text-sm text-blue-800">{data.acas.description}</p>
          </div>
          <ExternalLink className="h-5 w-5 text-blue-700" />
        </a>
      </main>
    </div>
  );
}
